/**
 * TenantContext - Provides tenant information throughout the app
 * Handles tenant detection from domain/subdomain and stores tenant configuration
 */
import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { apiClient } from '@/shared/utils/api';

export interface TenantConfig {
  tenant_id: string;
  name: string;
  code: string;
  schema_name: string;
  subscription_plan: 'free' | 'basic' | 'professional' | 'enterprise';

  // Branding
  logo_url?: string;
  primary_color: string;
  secondary_color: string;

  // Feature flags
  features: {
    referral_portal?: boolean;
    mobile_app?: boolean;
    advanced_reporting?: boolean;
    multi_location?: boolean;
    [key: string]: boolean | undefined;
  };

  // Limits
  max_locations: number;
  max_staff: number;
  max_monthly_visits: number;

  // Contact
  contact_email: string;
  contact_phone?: string;
}

interface TenantContextType {
  tenant: TenantConfig | null;
  loading: boolean;
  error: string | null;
  hasFeature: (featureName: string) => boolean;
  refetchTenant: () => Promise<void>;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

interface TenantProviderProps {
  children: ReactNode;
}

// Default tenant configuration for localhost development
const DEFAULT_TENANT: TenantConfig = {
  tenant_id: 'default',
  name: 'AnandLab',
  code: 'anandlab',
  schema_name: 'anandlab',
  subscription_plan: 'free',
  primary_color: '#3B82F6',
  secondary_color: '#1E40AF',
  features: {
    referral_portal: true,
    mobile_app: true,
    advanced_reporting: false,
    multi_location: false,
  },
  max_locations: 1,
  max_staff: 10,
  max_monthly_visits: 1000,
  contact_email: 'admin@anandlab.com',
};

export function TenantProvider({ children }: TenantProviderProps) {
  const [tenant, setTenant] = useState<TenantConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch tenant configuration from backend
   */
  const fetchTenant = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch tenant config from API (both dev and production)
      const response = await apiClient.get('/api/tenants/current/');
      setTenant(response.data);
    } catch (err) {
      console.error('Error fetching tenant:', err);
      setError('Failed to load tenant configuration');
      // Fallback to default tenant
      setTenant(DEFAULT_TENANT);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Check if tenant has a specific feature enabled
   */
  const hasFeature = (featureName: string): boolean => {
    if (!tenant) return false;
    return tenant.features[featureName] === true;
  };

  /**
   * Refetch tenant configuration (useful after updates)
   */
  const refetchTenant = async () => {
    await fetchTenant();
  };

  // Fetch tenant on mount
  useEffect(() => {
    fetchTenant();
  }, []);

  // Update page title when tenant changes
  // NOTE: CSS variables are managed by ThemeContext, not here
  useEffect(() => {
    if (tenant) {
      // Update page title
      document.title = `${tenant.name} - Medical Portal`;
    }
  }, [tenant]);

  const value: TenantContextType = {
    tenant,
    loading,
    error,
    hasFeature,
    refetchTenant,
  };

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
}

/**
 * Hook to access tenant context
 */
export function useTenant() {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
}

/**
 * Hook to check if a feature is enabled
 */
export function useFeature(featureName: string): boolean {
  const { hasFeature } = useTenant();
  return hasFeature(featureName);
}
